import { isFuture } from 'date-fns';
import { useState, SyntheticEvent } from 'react';
import { TaskError, TaskStatus, TaskView, ErrorType } from '@nexelem/graphne-api';
import { TabContext } from '@mui/lab';
import { DetailsPanel, DetailsTab, DetailsTabList } from '@nexelem/tabs';
import { useCustomTranslation } from '../../../../../../hooks';
import { LotTab } from './tabs/LotTab';
import { ConnectionsTab } from './tabs/ConnectionsTab';
import { useAuthContext } from '../../../../../../providers/AuthProvider';
import { PERMS } from '../../../../../../routes/permissions';

type TaskDetailsDrawerContentProps = {
  task: TaskView;
  isSuggested?: boolean;
  isGraphDrawer: boolean;
  isKanban: boolean;
};

const AVAILABLE_TABS = {
  LOT: 'lot',
  CONNECTIONS: 'connections',
};

export const TaskDetailsDrawerContent = ({
  task,
  isSuggested,
  isGraphDrawer,
  isKanban,
}: TaskDetailsDrawerContentProps) => {
  const { start, errors, status } = task;
  const [currentTab, setCurrentTab] = useState(AVAILABLE_TABS.LOT);
  const { tWrapper } = useCustomTranslation('products.productionPlan.taskDetailsDrawer');
  const getLastErrorMessage = useGetLastErrorMessage();
  const { hasPermission } = useAuthContext();
  const canEditPlan = hasPermission(PERMS.PERM_4);

  const isEditable =
    canEditPlan &&
    (status === TaskStatus.Pending || (status === TaskStatus.Error && start && isFuture(new Date(start))));

  const handleChange = (_: SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  };

  const errorMessage = getLastErrorMessage(errors);

  return (
    <TabContext value={currentTab}>
      <DetailsTabList variant="fullWidth" onChange={handleChange}>
        <DetailsTab label={tWrapper('lot')} value={AVAILABLE_TABS.LOT} />
        <DetailsTab label={tWrapper('connections')} value={AVAILABLE_TABS.CONNECTIONS} />
      </DetailsTabList>
      <div className="grow overflow-auto">
        <DetailsPanel value={AVAILABLE_TABS.LOT}>
          <LotTab errorMessage={errorMessage} isEditable={!!isEditable} isSuggested={isSuggested} task={task} />
        </DetailsPanel>
        <DetailsPanel value={AVAILABLE_TABS.CONNECTIONS}>
          <ConnectionsTab isGraphDrawer={isGraphDrawer} isKanban={isKanban} task={task} />
        </DetailsPanel>
      </div>
    </TabContext>
  );
};

const useGetLastErrorMessage = () => {
  const { tWrapper } = useCustomTranslation('products.productionPlan.taskDetailsDrawer.errors');

  return (errors: TaskError[] = []) => {
    const filteredErrors = errors.filter((error) => error.errorType !== ErrorType.Overdue);
    const hasMissingPlanDependencies = filteredErrors.some(
      (error) => error.errorType === ErrorType.MissingPlanDependencies,
    );
    const lastError = filteredErrors.at(-1);

    if (hasMissingPlanDependencies) {
      return tWrapper('MissingPlanDependencies');
    }

    return lastError?.message;
  };
};
